import { useTranslation } from 'react-i18next';
import { HpTag, TableScroll, type HpTagTone } from '@hp/ui';
import { SEGMENT_LABELS, type Segment, type Signal } from '@hp/domain';
import { PdLink } from '../../components/pd-link';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';
import type { RouterOutputs } from '../../api/trpc';

export type SegmentedDeal = RouterOutputs['segmentation']['deals']['items'][number];

const SEGMENT_TONE: Record<Segment, HpTagTone> = {
  investicni: 'info',
  rezidencni: 'success',
  nezarazeno: 'neutral',
};

/** Segment labels are the Pipedrive VALUES ("Investiční", …) — the same in every UI language. */
export function SegmentChip({ segment }: { segment: Segment }) {
  return <HpTag tone={SEGMENT_TONE[segment]}>{SEGMENT_LABELS[segment]}</HpTag>;
}

/** Signal pill: key (short) with side color; evidence + source in the tooltip. */
function SignalPill({ s }: { s: Signal }) {
  const { t } = useTranslation(['segmentation', 'common']);
  const short = s.key.replace(/^(inv|res)_/, '');
  const source = t(`signalSource.${s.source}`);
  return (
    <span
      className={s.side === 'inv' ? 'bo-signal bo-signal--inv' : 'bo-signal bo-signal--res'}
      title={s.evidence ? `${source}: ${s.evidence}` : source}
    >
      {short}
      {s.source === 'llm' ? '·ai' : ''}
    </span>
  );
}

export function DealTable({ deals, pdDomain }: { deals: SegmentedDeal[]; pdDomain: string | null | undefined }) {
  const { t } = useTranslation(['segmentation', 'common']);
  if (deals.length === 0) {
    return <p className="hp-card__body bo-muted">{t('table.empty')}</p>;
  }
  return (
    <TableScroll label={t('table.label')}>
      <BrutTable className="bo-deal-table">
        <BrutTheadRow>
          <th>{t('table.deal')}</th>
          <th>{t('table.person')}</th>
          <th>{t('common:table.status')}</th>
          <th>{t('table.segment')}</th>
          <th className="bo-cell-num">{t('table.score')}</th>
          <th>{t('table.signals')}</th>
          <th className="bo-cell-num">{t('table.personWon')}</th>
        </BrutTheadRow>
        <tbody>
          {deals.map((d) => (
            <tr key={d.dealPdId}>
              <td className="bo-cell-title">
                <span className="bo-clip bo-strong" title={d.title ?? undefined}>
                  {d.title ?? t('table.untitled')}
                </span>
                <PdLink domain={pdDomain} entity="deal" pdId={d.dealPdId} />
              </td>
              <td className="bo-cell-person">
                {d.personPdId ? (
                  <>
                    <span className="bo-clip">{d.personName ?? `#${d.personPdId}`}</span>
                    <PdLink domain={pdDomain} entity="person" pdId={d.personPdId} />
                  </>
                ) : (
                  <span className="bo-muted">—</span>
                )}
              </td>
              <td className="bo-cell-muted">
                {d.status ? t(`dealStatus.${d.status}`, { defaultValue: d.status }) : '—'}
              </td>
              <td>
                <SegmentChip segment={d.segment} />
              </td>
              <td className="bo-cell-num bo-strong">
                {d.scoreInv}:{d.scoreRes}
              </td>
              <td className="bo-cell-signals">
                {d.signals.length === 0 ? (
                  <span className="bo-muted">—</span>
                ) : (
                  <span className="bo-chips">
                    {d.signals.map((s, i) => (
                      <SignalPill key={i} s={s} />
                    ))}
                  </span>
                )}
              </td>
              <td className="bo-cell-num">{d.personWonDeals}</td>
            </tr>
          ))}
        </tbody>
      </BrutTable>
    </TableScroll>
  );
}
